import { ArrowRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import type { SchemaPattern } from '@/features/schema/types'
import type { ConceptSeverity } from '@/services/api/schemas'
import { cn } from '@/utils/cn'
import { RowFindingBadge } from './RowFindingBadge'

export interface PatternListItemProps {
  pattern: SchemaPattern
  isSelected: boolean
  onSelect: (pattern: SchemaPattern) => void
  /** Worst validation-finding severity on this pattern, after a Validate run. */
  severity?: ConceptSeverity
}

type PatternBadgeTone = 'entity' | 'relationship'

const patternBadgeToneClasses: Record<PatternBadgeTone, string> = {
  entity: 'bg-info-surface text-info-foreground border-info-border',
  relationship: 'bg-warning-surface text-warning-foreground border-warning-border font-mono',
}

function PatternBadge({ value, tone }: { value: string; tone: PatternBadgeTone }) {
  return (
    <Badge
      variant="outline"
      className={cn('min-w-0 shrink text-xs', patternBadgeToneClasses[tone])}
      title={value}
    >
      <span className="truncate">{value}</span>
    </Badge>
  )
}

/** A single source→rel→target pattern row in the patterns list column. */
export function PatternListItem({ pattern, isSelected, onSelect, severity }: PatternListItemProps) {
  return (
    <div
      onClick={() => onSelect(pattern)}
      className={cn(
        'flex max-md:flex-wrap items-center gap-2 p-3 rounded-lg cursor-pointer transition-all border',
        isSelected
          ? 'bg-muted border-success-border'
          : 'bg-card/50 border-border hover:border-border'
      )}
    >
      <PatternBadge value={pattern.source_type} tone="entity" />
      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
      <PatternBadge value={pattern.relationship_type} tone="relationship" />
      <ArrowRight className="h-3 w-3 text-muted-foreground shrink-0" />
      <PatternBadge value={pattern.target_type} tone="entity" />
      <RowFindingBadge severity={severity} className="ml-auto" />
    </div>
  )
}
